import { Controller } from "@hotwired/stimulus";

// Generic V2 stepper/accordion controller.
// Manages step expansion, step number styling, summary text, and chevron rotation.
// Domain-specific logic (validation, data loading) lives in a companion controller.
export default class extends Controller {
  static targets = [
    "step",
    "stepContent",
    "stepHeader",
    "stepSummary",
    "stepNumber",
    "stepLabel",
    "stepChevron",
  ];

  static values = {
    currentStep: { type: Number, default: 1 },
    freeNavigation: { type: Boolean, default: false },
  };

  connect() {
    this.showStep(this.currentStepValue);
  }

  // Navigate to a step (only if it's current or previously completed, unless free navigation is enabled)
  goToStep(event) {
    const step = parseInt(
      event.currentTarget.closest("[data-step]").dataset.step,
      10,
    );
    if (this.freeNavigationValue || step <= this.currentStepValue) {
      this.showStep(step);
    }
  }

  // Advance to the next step. Dispatches a "validate" event that companion
  // controllers can listen to and cancel if the step isn't ready.
  next() {
    const event = this.dispatch("validate", {
      detail: { step: this.currentStepValue },
      cancelable: true,
    });
    if (event.defaultPrevented) return;

    this.updateSummaryFromContent(this.currentStepValue);
    const totalSteps = this.stepTargets.length;
    if (this.currentStepValue < totalSteps) {
      this.currentStepValue++;
      this.showStep(this.currentStepValue);
    }
  }

  // Go back one step
  back() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--;
      this.showStep(this.currentStepValue);
    }
  }

  showStep(stepNumber) {
    this.stepTargets.forEach((el, index) => {
      const num = index + 1;
      const content = this.stepContentTargets[index];
      const summary = this.stepSummaryTargets[index];
      const stepNum = this.stepNumberTargets[index];
      const chevron = this.stepChevronTargets[index];

      const isActive = num === stepNumber;
      const isComplete = num < stepNumber;
      const isAccessible = isActive || isComplete || this.freeNavigationValue;

      // Content visibility
      if (isActive) {
        content.classList.remove("hidden");
      } else {
        content.classList.add("hidden");
      }

      // Summary visibility (show for completed, hide for active/future)
      if (isComplete && !isActive) {
        summary.classList.remove("hidden");
      } else {
        summary.classList.add("hidden");
      }

      // Step opacity
      if (isAccessible) {
        el.classList.remove("opacity-50");
      } else {
        el.classList.add("opacity-50");
      }

      // Step number styling — always show number, never replace with checkmark
      stepNum.textContent = String(num);
      const allStepNumClasses = [
        "bg-sg-gray-800", "text-white",
        "bg-sg-green", "text-sg-gray-900",
        "bg-sg-gray-100", "text-sg-gray-500",
        "opacity-40",
      ];
      stepNum.classList.remove(...allStepNumClasses);
      if (isActive) {
        stepNum.classList.add("bg-sg-gray-800", "text-white");
      } else if (isComplete) {
        // Completed steps use the green brand color as a clear "done" marker.
        // The opacity-40 fade-out treatment is reserved for legacy non-freeNav
        // callers that want a muted look; we no longer apply it here since
        // green carries the semantic by itself.
        stepNum.classList.add("bg-sg-green", "text-sg-gray-900");
      } else {
        stepNum.classList.add("bg-sg-gray-100", "text-sg-gray-500");
      }

      // Step label — fade for completed steps (skip fading in free navigation mode)
      const label = this.stepLabelTargets[index];
      if (label) {
        if (isComplete && !isActive && !this.freeNavigationValue) {
          label.classList.add("opacity-40");
        } else {
          label.classList.remove("opacity-40");
        }
      }

      // Chevron rotation
      if (chevron) {
        if (isActive) {
          chevron.classList.add("rotate-90");
        } else {
          chevron.classList.remove("rotate-90");
        }
      }

      // Header clickability
      const header = this.stepHeaderTargets[index];
      if (isAccessible) {
        header.classList.add("cursor-pointer");
      } else {
        header.classList.remove("cursor-pointer");
      }
    });

    this.currentStepValue = stepNumber;
    this.dispatch("stepChanged", { detail: { step: stepNumber } });
  }

  // Set summary text for a step programmatically
  setSummary(stepNumber, text) {
    const summary = this.stepSummaryTargets[stepNumber - 1];
    if (summary) {
      summary.textContent = text;
    }
  }

  // Auto-generate summary from a data-summary attribute on the step content
  updateSummaryFromContent(stepNumber) {
    const content = this.stepContentTargets[stepNumber - 1];
    if (!content) return;

    const summaryEl = content.querySelector("[data-stepper-summary]");
    if (summaryEl) {
      this.setSummary(stepNumber, summaryEl.textContent.trim());
    }
  }
}
